/**
 * Structures: where they go and when they are built. What they look like
 * lives in one file per family next to this one; `STRUCTURES` below is the
 * whole list — add a StructureType there to add a structure.
 *
 * Placement is vanilla's random spread (see StructureType in kit.ts). A
 * structure is a list of pieces generated from its start chunk alone, so every
 * chunk it touches regenerates the same pieces and writes only its own part.
 */

import { CHUNK_SIZE, WORLD_HEIGHT } from '../../constants';
import { hash2, mulberry32 } from '../../noise';
import { type GenCtx, MARGIN, PSIZE } from '../ctx';
import type { TerrainGenerator } from '../terrain';
import { int, type Piece, PIECE_OVERHANG, type StructureType } from './kit';
import { ANCIENT_CITY } from './ancientCity';
import { DESERT_PYRAMID, DESERT_WELL } from './desert';
import { IGLOO } from './igloo';
import { JUNGLE_TEMPLE } from './jungleTemple';
import { WOODLAND_MANSION } from './mansion';
import { OCEAN_MONUMENT, OCEAN_RUIN, SHIPWRECK } from './ocean';
import { PILLAGER_OUTPOST } from './outpost';
import { RUINED_PORTAL } from './ruinedPortal';
import { STRONGHOLD } from './stronghold';
import { SWAMP_HUT } from './swampHut';
import { TRAIL_RUINS } from './trailRuins';
import { BURIED_TREASURE, DUNGEON, FOSSIL, MINESHAFT } from './underground';
import { TRIAL_CHAMBERS } from './trialChambers';
import { VILLAGE } from './village';
import type { LootKind } from '../../game/loot';

/** Every structure that generates, in placement order (later ones win overlaps). */
export const STRUCTURES: readonly StructureType[] = [
  VILLAGE, DESERT_PYRAMID, JUNGLE_TEMPLE, IGLOO, SWAMP_HUT, PILLAGER_OUTPOST, WOODLAND_MANSION,
  RUINED_PORTAL, SHIPWRECK, OCEAN_RUIN, OCEAN_MONUMENT, TRAIL_RUINS, ANCIENT_CITY, STRONGHOLD,
  MINESHAFT, DESERT_WELL, FOSSIL, BURIED_TREASURE, DUNGEON, TRIAL_CHAMBERS,
];

const CACHE_MAX = 400;

/** A structure found by `StructureGenerator.nearby` (world coords, debug HUD). */
export interface StructureHit {
  name: string;
  x: number;
  z: number;
  y: number;
  dist: number;
}

export class StructureGenerator {
  private readonly cache = new Map<string, Piece[] | null>();

  constructor(private readonly gen: TerrainGenerator) {}

  /** The start chunk of region (rx, rz) for type k, and its random stream. */
  private start(k: number, rx: number, rz: number): { sx: number; sz: number; rnd: () => number } | null {
    const t = STRUCTURES[k];
    const rnd = mulberry32(Math.floor(hash2(rx, rz, this.gen.seed ^ t.salt) * 4294967296));
    if (t.site) {
      const at = t.site(this.gen.seed, rx, rz);
      return at && { sx: at.cx, sz: at.cz, rnd };
    }
    const span = t.spacing - t.separation;
    return { sx: rx * t.spacing + int(rnd, span), sz: rz * t.spacing + int(rnd, span), rnd };
  }

  /** The pieces of type k's structure starting in chunk (sx, sz), cached. */
  private pieces(k: number, sx: number, sz: number, rnd: () => number): Piece[] | null {
    const key = k + ':' + sx + ',' + sz;
    let pieces = this.cache.get(key);
    if (pieces === undefined) {
      if (this.cache.size >= CACHE_MAX) this.cache.clear();
      pieces = STRUCTURES[k].generate(this.gen, sx * CHUNK_SIZE + 8, sz * CHUNK_SIZE + 8, rnd);
      this.cache.set(key, pieces);
    }
    return pieces;
  }

  /** Every piece of every structure that could touch chunk (cx, cz). */
  private piecesNear(cx: number, cz: number): Piece[] {
    const out: Piece[] = [];
    for (let k = 0; k < STRUCTURES.length; k++) {
      const t = STRUCTURES[k];
      const r0x = Math.floor((cx - t.reach) / t.spacing), r1x = Math.floor((cx + t.reach) / t.spacing);
      const r0z = Math.floor((cz - t.reach) / t.spacing), r1z = Math.floor((cz + t.reach) / t.spacing);
      for (let rz = r0z; rz <= r1z; rz++) {
        for (let rx = r0x; rx <= r1x; rx++) {
          const st = this.start(k, rx, rz);
          if (!st) continue;
          const { sx, sz, rnd } = st;
          if (Math.abs(sx - cx) > t.reach || Math.abs(sz - cz) > t.reach) continue;
          const pieces = this.pieces(k, sx, sz, rnd);
          if (pieces) for (const p of pieces) out.push(p);
        }
      }
    }
    return out;
  }

  /** Loot table of the structure chest at (x, y, z), or null when no structure with its own table covers it. */
  lootAt(x: number, y: number, z: number): LootKind | null {
    const cx = Math.floor(x / CHUNK_SIZE), cz = Math.floor(z / CHUNK_SIZE);
    for (const p of this.piecesNear(cx, cz)) {
      if (p.loot && x >= p.x0 && x <= p.x1 && z >= p.z0 && z <= p.z1 && y >= p.y - 2 && y <= p.y + 16) return p.loot;
    }
    return null;
  }

  /** Nearest structures to world position (x, z), for the debug HUD: the
   *  closest instance of each surface kind (hidden kinds are skipped),
   *  deterministic like generation and sharing the piece cache. */
  nearby(x: number, z: number, limit: number, radiusBlocks = 768): StructureHit[] {
    const ccx = Math.floor(x / CHUNK_SIZE), ccz = Math.floor(z / CHUNK_SIZE);
    const hits: StructureHit[] = [];
    for (let k = 0; k < STRUCTURES.length; k++) {
      const t = STRUCTURES[k];
      if (t.hud === false) continue;
      // regions whose candidate chunk can land within radiusBlocks
      const rr = Math.ceil(radiusBlocks / CHUNK_SIZE / t.spacing) + 1;
      const bx = Math.floor(ccx / t.spacing), bz = Math.floor(ccz / t.spacing);
      let best: StructureHit | null = null;
      for (let rz = bz - rr; rz <= bz + rr; rz++) {
        for (let rx = bx - rr; rx <= bx + rr; rx++) {
          const st = this.start(k, rx, rz);
          if (!st) continue;
          const pieces = this.pieces(k, st.sx, st.sz, st.rnd);
          if (!pieces) continue;
          const p0 = pieces[0];
          const cx = (p0.x0 + p0.x1) / 2, cz = (p0.z0 + p0.z1) / 2;
          const dist = Math.hypot(cx - x, cz - z);
          if (dist > radiusBlocks || (best && dist >= best.dist)) continue;
          best = { name: t.name, x: cx, z: cz, y: p0.y, dist };
        }
      }
      if (best) hits.push(best);
    }
    hits.sort((a, b) => a.dist - b.dist);
    return hits.slice(0, limit);
  }

  /** Level the terrain under buildings: pull the surface to the floor and damp the 3-D noise. */
  flatten(ctx: GenCtx, colH: Float32Array, colA: Float32Array): void {
    const x0 = ctx.ox - MARGIN, z0 = ctx.oz - MARGIN;
    const B = 5;
    for (const p of this.piecesNear(ctx.cx, ctx.cz)) {
      if (!p.flat) continue;
      const ax = Math.max(p.x0 - B, x0), bx = Math.min(p.x1 + B, x0 + PSIZE - 1);
      const az = Math.max(p.z0 - B, z0), bz = Math.min(p.z1 + B, z0 + PSIZE - 1);
      for (let z = az; z <= bz; z++) {
        for (let x = ax; x <= bx; x++) {
          const dx = Math.max(p.x0 - x, 0, x - p.x1), dz = Math.max(p.z0 - z, 0, z - p.z1);
          const t = Math.max(0, 1 - Math.max(dx, dz) / B);
          const w = t * t * (3 - 2 * t);
          const i = x - x0 + (z - z0) * PSIZE;
          colH[i] += (p.y + 0.5 - colH[i]) * w;
          colA[i] *= 1 - w;
        }
      }
    }
  }

  /** Build every piece that reaches into this chunk. */
  place(ctx: GenCtx): void {
    const x1 = ctx.ox + CHUNK_SIZE - 1, z1 = ctx.oz + CHUNK_SIZE - 1;
    const o = PIECE_OVERHANG;
    for (const p of this.piecesNear(ctx.cx, ctx.cz)) {
      if (p.x1 + o < ctx.ox || p.x0 - o > x1 || p.z1 + o < ctx.oz || p.z0 - o > z1) continue;
      if (p.y < 2 || p.y > WORLD_HEIGHT - 30) continue;
      p.build(ctx);
    }
  }
}
